import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import LoanParametersSection from './LoanParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import EarlyRepaymentSection from './EarlyRepaymentSection';
import FeesSection from './FeesSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета кредита
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  loanTypes,
  earlyRepaymentPeriods,
  saveLoan 
}) => {
  
  // Состояние для имени сохраняемого кредита
  const [saveLoanName, setSaveLoanName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем процентную ставку автоматически при изменении типа кредита
      if (name === 'loanPurpose') {
        const selectedLoanType = loanTypes.find(type => type.id === fieldValue);
        if (selectedLoanType) {
          const currentRate = parseFloatOrDefault(prev.interestRate, 0);
          const isRateDefault = loanTypes.some(lt => currentRate === lt.defaultRate);
          if (isRateDefault || !prev.interestRate || currentRate <= 0) {
            newState.interestRate = selectedLoanType.defaultRate.toString();
          }
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeEarlyRepayment' && !checked) {
        newState.earlyRepaymentType = 'reduction';
        newState.earlyRepaymentAmount = '50000';
        newState.earlyRepaymentPeriod = 'monthly';
        newState.earlyRepaymentStartMonth = '6';
      }
      
      if (name === 'includeFees' && !checked) {
        newState.initialFee = '0';
        newState.monthlyFee = '0';
        newState.monthlyFeeType = 'fixed';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [loanTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      loanAmount, loanTerm, interestRate, startDate,
      includeEarlyRepayment, earlyRepaymentAmount, earlyRepaymentStartMonth, earlyRepaymentPeriod, earlyRepaymentType,
      includeFees, initialFee, monthlyFee, monthlyFeeType, paymentType
    } = formData;

    // Парсинг числовых значений
    const numLoanAmount = parseFloatOrDefault(loanAmount, -1);
    const numLoanTermYears = parseFloatOrDefault(loanTerm, -1);
    const numLoanTermMonths = Math.round(numLoanTermYears * 12);
    const numInterestRate = parseFloatOrDefault(interestRate, -1);

    // Валидация базовых полей
    if (numLoanAmount <= 0) newErrors.loanAmount = 'Сумма должна быть положительной.';
    if (!newErrors.loanAmount && numLoanAmount > 50000000) newErrors.loanAmount = 'Максимальная сумма 50 млн ₽.';
    
    if (numLoanTermYears <= 0) newErrors.loanTerm = 'Срок должен быть положительным.';
    if (!newErrors.loanTerm && numLoanTermYears > 30) newErrors.loanTerm = 'Максимальный срок 30 лет.';
    if (!newErrors.loanTerm && numLoanTermMonths <= 0) newErrors.loanTerm = 'Срок должен быть не менее 1 месяца.';
    
    if (numInterestRate <= 0) newErrors.interestRate = 'Ставка должна быть положительной.';
    if (!newErrors.interestRate && numInterestRate > 100) newErrors.interestRate = 'Ставка не может быть более 100%.';

    // Проверка даты начала кредита
    if (!startDate) newErrors.startDate = 'Укажите дату начала кредита.';
    try {
      const dateObject = new Date(startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    // Валидация досрочного погашения
    if (includeEarlyRepayment) {
      const numEarlyRepaymentAmount = parseFloatOrDefault(earlyRepaymentAmount, -1);
      const numEarlyRepaymentStartMonth = parseIntOrDefault(earlyRepaymentStartMonth, -1);
      if (numEarlyRepaymentAmount <= 0) newErrors.earlyRepaymentAmount = 'Сумма должна быть положительной.';
      if (!newErrors.earlyRepaymentAmount && numEarlyRepaymentAmount >= numLoanAmount) {
        newErrors.earlyRepaymentAmount = 'Сумма досрочного погашения не может быть больше или равна сумме кредита.';
      }
      if (!isInteger(earlyRepaymentStartMonth) || numEarlyRepaymentStartMonth <= 0) {
        newErrors.earlyRepaymentStartMonth = 'Введите целый полож. номер месяца.';
      }
      if (!newErrors.loanTerm && !newErrors.earlyRepaymentStartMonth && numEarlyRepaymentStartMonth > numLoanTermMonths) {
        newErrors.earlyRepaymentStartMonth = `Начало (${numEarlyRepaymentStartMonth}) не может быть позже срока (${numLoanTermMonths} мес.).`;
      }
      if (!earlyRepaymentPeriod) newErrors.earlyRepaymentPeriod = "Выберите периодичность.";
      if (!earlyRepaymentType) newErrors.earlyRepaymentType = "Выберите цель погашения.";
    }

    // Валидация комиссий
    if (includeFees) {
      const numInitialFee = parseFloatOrDefault(initialFee, -1);
      const numMonthlyFee = parseFloatOrDefault(monthlyFee, -1);
      
      if (numInitialFee < 0) newErrors.initialFee = 'Значение не может быть отрицательным.';
      if (numMonthlyFee < 0) newErrors.monthlyFee = 'Значение не может быть отрицательным.';
      
      if (monthlyFeeType === 'percent' && numMonthlyFee > 100) {
        newErrors.monthlyFee = 'Процент не может быть больше 100%.';
      }
    }

    // Валидация типа платежа
    if (!paymentType || !['annuity', 'differentiated'].includes(paymentType)) {
      newErrors.paymentType = "Выберите корректный тип платежа.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет аннуитетного платежа
  const calculateAnnuityPayment = (principal, termMonths, annualRate) => {
    if (principal <= 0 || termMonths <= 0) return 0;
    const monthlyRate = annualRate / 12 / 100;

    if (monthlyRate === 0) {
      return principal / termMonths;
    }

    const factor = Math.pow(1 + monthlyRate, termMonths);
    if (!isFinite(factor) || factor === 1) {
      return principal / termMonths;
    }
    return principal * monthlyRate * factor / (factor - 1);
  };

  // Функция расчета графика платежей
  const calculatePaymentSchedule = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numLoanAmount = parseFloatOrDefault(formData.loanAmount);
        const initialLoanAmount = numLoanAmount;

        if (initialLoanAmount <= 0) {
          setErrors({ general: "Сумма кредита должна быть положительной." });
          setIsCalculating(false);
          return;
        }

        const initialLoanTermYears = parseFloatOrDefault(formData.loanTerm);
        const initialLoanTermMonths = Math.round(initialLoanTermYears * 12);
        const initialAnnualRate = parseFloatOrDefault(formData.interestRate);
        const paymentType = formData.paymentType;
        const startDateObj = new Date(formData.startDate);

        const includeEarlyRepayment = formData.includeEarlyRepayment;
        const earlyRepaymentType = formData.earlyRepaymentType;
        const earlyRepaymentAmount = includeEarlyRepayment ? parseFloatOrDefault(formData.earlyRepaymentAmount, 0) : 0;
        const earlyRepaymentPeriod = formData.earlyRepaymentPeriod;
        const earlyRepaymentStartMonth = includeEarlyRepayment ? parseIntOrDefault(formData.earlyRepaymentStartMonth, 1) : 1;

        const includeFees = formData.includeFees;
        const initialFee = includeFees ? parseFloatOrDefault(formData.initialFee, 0) : 0;
        const monthlyFee = includeFees ? parseFloatOrDefault(formData.monthlyFee, 0) : 0;
        const monthlyFeeType = formData.monthlyFeeType;

        // --- Инициализируем переменные для расчета ---
        let currentLoanBalance = initialLoanAmount;
        let remainingTermMonthsForCalc = initialLoanTermMonths;
        let totalPrincipalPaidScheduled = 0;
        let totalInterestPaid = 0;
        let totalInitialFeePaid = initialFee;
        let totalMonthlyFeesPaid = 0;
        let totalEarlyRepaymentPaid = 0;

        const calculatedSchedule = [];
        let currentMonth = 0;

        // Рассчитываем начальный аннуитетный платеж (если применимо)
        let fixedAnnuityPayment = (paymentType === 'annuity')
            ? calculateAnnuityPayment(initialLoanAmount, initialLoanTermMonths, initialAnnualRate)
            : 0;

        // Определяем частоту досрочного погашения
        let earlyRepaymentFrequencyMonths = 0;
        if (includeEarlyRepayment && earlyRepaymentAmount > 0) {
            switch (earlyRepaymentPeriod) {
                case 'monthly': earlyRepaymentFrequencyMonths = 1; break;
                case 'quarterly': earlyRepaymentFrequencyMonths = 3; break;
                case 'semi_annually': earlyRepaymentFrequencyMonths = 6; break;
                case 'annually': earlyRepaymentFrequencyMonths = 12; break;
                case 'once': earlyRepaymentFrequencyMonths = -1; break;
                default: earlyRepaymentFrequencyMonths = 0;
            }
        }

        // --- Цикл расчета графика платежей ---
        const maxIterations = initialLoanTermMonths * 2; // Некоторый запас итераций

        while (currentLoanBalance > 0.005 && currentMonth < maxIterations) {
            currentMonth++;
            const paymentDate = new Date(startDateObj);
            paymentDate.setMonth(startDateObj.getMonth() + currentMonth - 1);

            // Рассчитываем ежемесячные проценты
            const monthlyRate = initialAnnualRate / 12 / 100;
            let interestPayment = currentLoanBalance * monthlyRate;
            interestPayment = Math.max(0, interestPayment);

            // Рассчитываем плановое погашение основного долга и ежемесячный платеж
            let principalPaymentScheduled = 0;
            let monthlyPaymentBase = 0;

            if (paymentType === 'annuity') {
                monthlyPaymentBase = fixedAnnuityPayment;
                principalPaymentScheduled = monthlyPaymentBase - interestPayment;

                if (currentLoanBalance + interestPayment <= monthlyPaymentBase + 0.005) {
                    principalPaymentScheduled = currentLoanBalance;
                    monthlyPaymentBase = currentLoanBalance + interestPayment;
                }
            } else { // Дифференцированный
                const basePrincipalPayment = initialLoanAmount / initialLoanTermMonths;
                principalPaymentScheduled = basePrincipalPayment;

                if (currentLoanBalance <= basePrincipalPayment + 0.005) {
                    principalPaymentScheduled = currentLoanBalance;
                }
                monthlyPaymentBase = principalPaymentScheduled + interestPayment;
            }
            principalPaymentScheduled = Math.max(0, principalPaymentScheduled);

            // Рассчитываем ежемесячную комиссию (если есть)
            let monthlyFeePayment = 0;
            if (includeFees && monthlyFee > 0) {
                if (monthlyFeeType === 'percent') {
                    monthlyFeePayment = currentLoanBalance * (monthlyFee / 100) / 12;
                } else { // fixed
                    monthlyFeePayment = monthlyFee;
                }
                totalMonthlyFeesPaid += monthlyFeePayment;
            }

            // Рассчитываем досрочное погашение
            let currentEarlyRepayment = 0;
            if (includeEarlyRepayment && earlyRepaymentAmount > 0 && currentMonth >= earlyRepaymentStartMonth && currentLoanBalance > 0) {
                const isRepaymentMonth =
                    (earlyRepaymentFrequencyMonths === -1 && currentMonth === earlyRepaymentStartMonth) ||
                    (earlyRepaymentFrequencyMonths > 0 && (currentMonth - earlyRepaymentStartMonth) % earlyRepaymentFrequencyMonths === 0);

                if (isRepaymentMonth) {
                    const maxEarlyRepayment = Math.max(0, currentLoanBalance - principalPaymentScheduled);
                    currentEarlyRepayment = Math.min(earlyRepaymentAmount, maxEarlyRepayment);
                }
            }

            // Рассчитываем общую сумму платежей за месяц
            const totalPaymentThisMonth = monthlyPaymentBase + monthlyFeePayment + currentEarlyRepayment;

            // Обновляем итоговые суммы
            totalPrincipalPaidScheduled += principalPaymentScheduled;
            totalInterestPaid += interestPayment;
            totalEarlyRepaymentPaid += currentEarlyRepayment;

            // Обновляем остаток кредита
            const startingBalance = currentLoanBalance;
            currentLoanBalance -= principalPaymentScheduled;
            currentLoanBalance -= currentEarlyRepayment;
            currentLoanBalance = Math.max(0, currentLoanBalance);

            // Пересчитываем аннуитетный платеж после досрочного погашения
            if (currentEarlyRepayment > 0 && paymentType === 'annuity' && currentLoanBalance > 0.005) {
                remainingTermMonthsForCalc = initialLoanTermMonths - currentMonth;
                if (remainingTermMonthsForCalc > 0) {
                    if (earlyRepaymentType === 'payment') {
                        // Для уменьшения платежа - пересчитываем аннуитет с новым остатком и тем же сроком
                        fixedAnnuityPayment = calculateAnnuityPayment(currentLoanBalance, remainingTermMonthsForCalc, initialAnnualRate);
                    } else {
                        // Для уменьшения срока - оставляем тот же платеж
                    }
                } else {
                    fixedAnnuityPayment = 0;
                }
            }

            // Добавляем запись в график платежей
            calculatedSchedule.push({
                month: currentMonth,
                date: paymentDate,
                payment: monthlyPaymentBase,
                principalPayment: principalPaymentScheduled,
                interestPayment: interestPayment,
                startingBalance: startingBalance,
                remainingBalance: currentLoanBalance,
                earlyRepayment: currentEarlyRepayment,
                monthlyFee: monthlyFeePayment,
                totalPayment: totalPaymentThisMonth
            });
        }

        if (currentMonth >= maxIterations && currentLoanBalance > 0.005) {
            console.warn("Расчет прерван из-за превышения максимального количества итераций.");
            setErrors({ general: "Расчет прерван из-за превышения максимального количества итераций. Проверьте параметры, особенно срок и досрочное погашение." });
            setResults(null);
            setSchedule([]);
            setIsCalculating(false);
            return;
        }

        const effectiveTermMonths = calculatedSchedule.length;

        // --- Готовим итоговые результаты ---
        const totalPrincipalPaid = totalPrincipalPaidScheduled + totalEarlyRepaymentPaid;
        const totalCreditCost = totalInterestPaid + totalInitialFeePaid + totalMonthlyFeesPaid;
        const totalPaymentOverall = initialLoanAmount + totalCreditCost;
        const actualMonthlyPayment = effectiveTermMonths > 0 ? calculatedSchedule.slice(0, Math.min(12, effectiveTermMonths)).reduce((sum, item) => sum + item.payment, 0) / Math.min(12, effectiveTermMonths) : 0;
        
        const finalResults = {
            loanAmount: initialLoanAmount,
            interestRate: initialAnnualRate,
            loanTermYears: initialLoanTermYears,
            loanTermMonths: initialLoanTermMonths,
            paymentType: paymentType,

            effectiveTermMonths: effectiveTermMonths,
            firstMonthlyPayment: calculatedSchedule.length > 0 ? calculatedSchedule[0].payment : 0,
            averageMonthlyPayment: actualMonthlyPayment,
            lastMonthlyPayment: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].payment : 0,
            
            totalPrincipalPaid: totalPrincipalPaid,
            totalInterest: totalInterestPaid,
            totalFees: totalInitialFeePaid + totalMonthlyFeesPaid,
            totalInitialFee: totalInitialFeePaid,
            totalMonthlyFees: totalMonthlyFeesPaid,
            
            totalEarlyRepayment: totalEarlyRepaymentPaid,
            totalCreditCost: totalCreditCost,
            totalPayment: totalPaymentOverall,
            overpaymentRatio: initialLoanAmount > 0 ? totalCreditCost / initialLoanAmount : 0,
            
            loanStartDate: startDateObj,
            loanEndDate: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].date : null,
            
            scheduleShort: calculatedSchedule.slice(0, 12) // Первые 12 месяцев для предпросмотра
        };

        setResults(finalResults);
        setSchedule(calculatedSchedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      loanAmount: '500000',
      loanTerm: '3',
      interestRate: '12.5',
      paymentType: 'annuity',
      startDate: new Date().toISOString().split('T')[0],
      loanPurpose: 'consumer',
      includeEarlyRepayment: false,
      earlyRepaymentType: 'reduction',
      earlyRepaymentAmount: '50000',
      earlyRepaymentPeriod: 'monthly',
      earlyRepaymentStartMonth: '6',
      includeFees: false,
      initialFee: '5000',
      monthlyFee: '0',
      monthlyFeeType: 'fixed',
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
    setSaveLoanName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика платежей в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Месяц', 'Дата', 'Платеж (Осн. долг + %)', 'Основной долг', 'Проценты'];
    if (results.totalMonthlyFees > 0) headers.push('Комиссия');
    if (results.totalEarlyRepayment > 0) headers.push('Досрочное погашение');
    headers.push('Общий платеж за месяц', 'Остаток долга');

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = schedule.map(item => {
      const rowData = [
        item.month,
        item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        formatCsvNumber(item.payment),
        formatCsvNumber(item.principalPayment),
        formatCsvNumber(item.interestPayment),
      ];
      if (results.totalMonthlyFees > 0) rowData.push(formatCsvNumber(item.monthlyFee));
      if (results.totalEarlyRepayment > 0) rowData.push(formatCsvNumber(item.earlyRepayment));
      rowData.push(
        formatCsvNumber(item.totalPayment),
        formatCsvNumber(item.remainingBalance)
      );
      return rowData.join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(schedule.reduce((sum, item) => sum + item.payment, 0)),
      formatCsvNumber(results.totalPrincipalPaid),
      formatCsvNumber(results.totalInterest)
    ];
    if (results.totalMonthlyFees > 0) totalRow.push(formatCsvNumber(results.totalMonthlyFees));
    if (results.totalEarlyRepayment > 0) totalRow.push(formatCsvNumber(results.totalEarlyRepayment));
    totalRow.push(formatCsvNumber(results.totalPayment), '');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Сумма кредита:', formatCsvNumber(results.loanAmount)],
      ['Срок кредита:', `${results.loanTermMonths} мес.`],
      ['Процентная ставка:', `${results.interestRate}%`],
      ['Тип платежа:', formData.paymentType === 'annuity' ? 'Аннуитетный' : 'Дифференцированный'],
      ['Дата начала кредита:', new Date(formData.startDate).toLocaleDateString('ru-RU')],
      ['Переплата:', formatCsvNumber(results.totalCreditCost)],
      ['Полная стоимость кредита:', formatCsvNumber(results.totalPayment)],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'кредитный_график_платежей.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения кредита
  const handleSaveLoan = () => {
    if (showSaveDialog) {
      const savedResult = saveLoan(saveLoanName || null);
      if (savedResult) {
        alert(`Кредит "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveLoanName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatePaymentSchedule();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры кредита
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры кредита */}
        <LoanParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          loanTypes={loanTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Досрочное погашение */}
        <EarlyRepaymentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          earlyRepaymentPeriods={earlyRepaymentPeriods}
        />

        {/* Комиссии */}
        <FeesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения кредита */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveLoanName}
                onChange={(e) => setSaveLoanName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveLoan}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать кредит'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveLoan}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;